/**
 * Helper functions for artifact-related operations
 */

import { LICENSE_ASSESSMENT_ORIGINAL_CODE } from '../common/artifacts/licenseAssessmentOriginal';
import { AGENTFORCE_ARTIFACT_CODE } from '../common/artifacts/agentforceArtifact';
import { INTEGRATION_ARTIFACT_CODE } from '../common/artifacts/integrationArtifact';
import { TECHDEBT_ARTIFACT_CODE } from '../common/artifacts/techDebtArtifact';
import { DEFAULT_REACT_ARTIFACT_CODE } from '../common/artifacts/defaultReactArtifact';

/**
 * Determines the content type of an artifact based on its content
 * @param content - The artifact content to analyze
 * @returns The detected content type ('react', 'html', or 'javascript')
 */
export const getContentType = (content: string): 'react' | 'html' | 'javascript' => {
  // If content starts with HTML doctype or html tag, it's HTML
  const trimmedContent = content.trim();
  if (trimmedContent.startsWith('<!DOCTYPE html>') || trimmedContent.startsWith('<html')) {
    return 'html';
  }

  // If content contains React/JSX patterns, it's React
  if (
    trimmedContent.includes('import React') ||
    trimmedContent.includes('useState') ||
    trimmedContent.includes('useEffect') ||
    trimmedContent.includes('export default') ||
    trimmedContent.includes('function App()') ||
    trimmedContent.includes('const App = ()') ||
    trimmedContent.includes('return (') || // JSX return statement
    /<[A-Z]/.test(trimmedContent) || // JSX component tags like <div>
    /style=\{\{/.test(trimmedContent) // Inline React styles
  ) {
    return 'react';
  }

  // Otherwise, assume it's JavaScript
  return 'javascript';
};

/**
 * Checks if an artifact ID is a demo/mock artifact
 * @param artifactId - The ID of the artifact
 * @returns true if the artifact is a demo artifact
 */
export const isDemoArtifact = (artifactId: string): boolean => {
  return (
    artifactId.startsWith('mock-') ||
    artifactId.startsWith('license-artifact-demo') ||
    artifactId.startsWith('agentforce-artifact-demo') ||
    artifactId.startsWith('integration-artifact-demo') ||
    artifactId.startsWith('techdebt-artifact-demo')
  );
};

/**
 * Gets demo artifact content based on artifact ID
 * NOTE: This is for demo/development purposes only.
 * In production, artifact content will come from the AI API.
 * @param artifactId - The ID of the demo artifact to get content for
 * @returns The demo artifact content string
 */
export const getDemoArtifactContent = (artifactId: string): string => {
  if (artifactId === 'license-artifact-demo-001') {
    return LICENSE_ASSESSMENT_ORIGINAL_CODE;
  }

  if (artifactId === 'agentforce-artifact-demo-001') {
    return AGENTFORCE_ARTIFACT_CODE;
  }

  if (artifactId === 'integration-artifact-demo-001') {
    return INTEGRATION_ARTIFACT_CODE;
  }

  if (artifactId === 'techdebt-artifact-demo-001') {
    return TECHDEBT_ARTIFACT_CODE;
  }

  // Default to the original opportunity analysis React code
  return DEFAULT_REACT_ARTIFACT_CODE;
};
